import {
  DeleteSeason,
  EditMenuLink,
  EditVideo,
  FirestickConfig,
  FooterRow,
  MenuLink,
  MultimediaCategory,
  PushNotification,
  Streaming,
  VimeoUser,
} from "./../types";
import keys from "../config/env";
import storageServices from "./storage";

// import env from "../config/env";
import {
  Ad,
  AdCircuit,
  Banner,
  ProgramCategory,
  EditAd,
  EditAdCircuit,
  EditBanner,
  EditProgram,
  Icon,
  Media,
  MediaFolder,
  News,
  Program,
  User,
  Video,
} from "../types";
import { toMedia, toMediaFolder, toUser, toVimeoUser } from "./utils";
import { v4 as uuidv4 } from 'uuid';

let sessionToken: string | undefined;
let page: string | undefined;
let limit: string | undefined;

const setSession = (token: string) => {
  sessionToken = token;
};

const clearSession = () => {
  sessionToken = undefined;
};

const setPage = (_page: string) => {
  page = _page;
};

const setLimit = (_limit: string) => {
  limit = _limit;
};

const makeApiRequest = async (
  method: "POST" | "GET" | "PATCH" | "DELETE",
  path: string,
  body?: any
): Promise<any> => {
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    mode: "no-cors",
  });

  if (sessionToken) {
    headers.append("Authorization", `Bearer ${sessionToken}`);
  }

  if (page) {
    headers.append("page", page);
  }

  if (limit) {
    headers.append("limit", limit);
  }

  try {
    const response = await fetch(`${keys.API_BASE_URL}${path}`, {
      method: method,
      body: body ? JSON.stringify(body) : undefined,
      headers: headers,
    });

    if (!response.ok) {
      const message = await parseErrorMessage(response);
      throw new Error(`${response.status}: ${response.statusText}. ${message}`);
    }
    const responseJson = await response.json();
    return responseJson;
  } catch (err) {
    console.warn(`Failed request ${method} ${path}`, err);
    throw err;
  }
};

const parseErrorMessage = async (response: Response) => {
  try {
    const data = await response.json();
    if (!data.ErrorMessage) {
      throw new Error();
    }
    return data.ErrorMessage;
  } catch (err) {
    return "";
  }
};

/**
 *
 * @returns START AUTHENTICATION
 */

const login = async (email: User["email"], password: string): Promise<User> => {
  storageServices.removeItem("session");
  const body = {
    email,
    password,
  };

  const response = await makeApiRequest("POST", "admin/basic", body);
  storageServices.setItem("session", { token: response.token });
  setSession(response.token);
  return toUser(response.admin);
};

const getCurrentUser = async (): Promise<User | null> => {
  const sessionData: { token: string } = storageServices.getItem("session") as {
    token: string;
  };

  if (!sessionData) return null;
  setSession(sessionData.token);
  const result = await makeApiRequest("GET", `admin/me`);

  return toUser(result.admin);
};

const changePassword = async (
  email: User["email"],
  password: string,
  token: string
): Promise<boolean> => {
  await makeApiRequest("POST", "admin/basic/change-password", {
    email,
    password,
    token,
  });

  return true;
};

const lostPassword = async (email: User["email"]): Promise<boolean> => {
  await makeApiRequest(
    "GET",
    `admin/basic/lost-password?email=${encodeURIComponent(email)}`
  );

  return true;
};

const changeSessionUserPassword = async (
  email: User["email"],
  password: string
): Promise<boolean> => {
  await makeApiRequest("PATCH", "admin/basic/change-password", {
    email,
    password,
  });

  return true;
};

/**
 *
 * @returns END AUTHENTICATION
 */

/**
 *
 * @returns START USERS
 */

const getUsers = async (): Promise<User[]> => {
  return [
    {
      id: "1",
      nominative: "",
      email: "",
      password: "",
    },
  ];
};

const addUser = async (data: Partial<User>): Promise<User> => {
  return {
    id: "1",
    nominative: "",
    email: "",
    password: "",
  };
};

const updateUser = async (data: Partial<User>): Promise<User> => {
  return {
    id: "1",
    nominative: "",
    email: "",
    password: "",
  };
};

const deleteUser = async (data: Partial<User>): Promise<any> => {
  return {
    id: "1",
    nominative: "",
    email: "",
    password: "",
  };
};

/**
 *
 * @returns END USERS
 */

/**
 *
 * @returns START ARTICLES
 */

const getArticles = async (): Promise<News[]> => {
  return [
    {
      id: 3,
      title: "Titolo3",
      description: "Titolo3",
      authorID: "Sanpronio",
      image: "",
      createAt: new Date(),
      categoryID: "Titolo3",
    },
  ];
};

const addArticle = async (data: Partial<News>): Promise<News> => {
  return {
    id: 3,
    title: "Titolo3",
    description: "Titolo3",
    authorID: "Sanpronio",
    image: "",
    createAt: new Date(),
    categoryID: "Titolo3",
  };
};

const updateArticle = async (data: Partial<News>): Promise<News> => {
  return {
    id: 3,
    title: "Titolo3",
    description: "Titolo3",
    authorID: "Sanpronio",
    image: "",
    createAt: new Date(),
    categoryID: "Titolo3",
  };
};

const deleteArticle = async (data: Partial<News>): Promise<any> => {
  return [
    {
      id: 3,
      title: "Titolo3",
      description: "Titolo3",
      authorID: "Sanpronio",
      image: "",
      createAt: new Date(),
      categoryID: "Titolo3",
    },
  ];
};

/**
 *
 * @returns END ARTICLES
 */

/**
 *
 * @returns START PROGRAM CATEGORY
 */

const getProgramCategories = async (): Promise<ProgramCategory[]> => {
  let response = await makeApiRequest("GET", "program-categories");
  return response.programCategories;
};

const addProgramCategory = async (
  data: ProgramCategory
): Promise<ProgramCategory> => {
  let response = await makeApiRequest("POST", "program-categories", data);
  return response.programCategory;
};

const updateProgramCategory = async (
  data: Partial<ProgramCategory>
): Promise<ProgramCategory> => {
  let response = await makeApiRequest(
    "PATCH",
    "program-categories/" + data.id,
    data
  );
  return response.programCategory;
};

const updateProgramCategoriesOrder = async (
  category: ProgramCategory,
  oldIndex: number,
  newIndex: number
): Promise<boolean> => {
  const response = await makeApiRequest(
    "PATCH",
    `program-categories/update-categories-order/`,
    {
      category,
      oldIndex,
      newIndex,
    }
  );
  return response.success;
};

const deleteProgramCategory = async (data: ProgramCategory): Promise<any> => {
  let response = await makeApiRequest(
    "DELETE",
    "program-categories/" + data.id
  );
  return response;
};

const uploadFileToBucket = async (file: File): Promise<string> => {
    const toBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const base64File = await toBase64(file);

    // Ottenere l'estensione del file originale
    const fileExtension = file.name.split('.').pop() || 'png'; // Default a png se non trova l'estensione

    // Generare un nome univoco con uuidv4
    const uniqueFileName = `${uuidv4()}.${fileExtension}`;

    const response = await makeApiRequest("POST", "admin/basic/upload-file-to-bucket", {
        file: base64File,
        name: uniqueFileName, // Usa il nome generato univoco
    });

    return response.fileUrl;
};
  

/**
 *
 * @returns END PROGRAM CATEGORY
 */

/**
 *
 * @returns START PROGRAM
 */

const getProgram = async (): Promise<Program[]> => {
  const response = await makeApiRequest(
    "GET",
    "program?page=1&limit=9999&includeProgramsWithoutEpisodes=true&includeInactive=true&includeDeleted=true"
  );
  return response.program;
};

const getProgramByID = async (id: string): Promise<Program> => {
  const response = await makeApiRequest(
    "GET",
    "program/" +
      id +
      "?includeEpisodeData=true&includeInactive=true&includeDeleted=true"
  );

  return response.program;
};

const addProgram = async (data: EditProgram): Promise<Program> => {
  const response = await makeApiRequest("POST", "program", data);
  return response.program;
};

const updateProgram = async (data: EditProgram): Promise<Program> => {
  const response = await makeApiRequest("PATCH", "program/" + data.id, data);
  return response.program;
};

const deleteProgram = async (id: string): Promise<any> => {
  const response = await makeApiRequest("DELETE", "program/" + id);
  return response;
};

const deleteSeason = async (id: number, data: DeleteSeason): Promise<any> => {
  const response = await makeApiRequest(
    "DELETE",
    "program/delete-season/" + id,
    data
  );
  return response;
};

/**
 *
 * @returns END PROGRAM
 */

/**
 *
 * @returns START MULTIMEDIA CATEGORY
 */

const getMultimediaCategories = async (): Promise<MultimediaCategory[]> => {
  let response = await makeApiRequest("GET", "multimedia-categories");
  return response.multimediaCategories;
};

const addMultimediaCategory = async (
  data: MultimediaCategory
): Promise<MultimediaCategory> => {
  let response = await makeApiRequest("POST", "multimedia-categories", data);
  return response.multimediaCategory;
};

const updateMultimediaCategory = async (
  data: Partial<MultimediaCategory>
): Promise<MultimediaCategory> => {
  let response = await makeApiRequest(
    "PATCH",
    "multimedia-categories/" + data.id,
    data
  );
  return response.multimediaCategory;
};

const updateMultimediaCategoriesOrder = async (
  category: MultimediaCategory,
  oldIndex: number,
  newIndex: number
): Promise<boolean> => {
  const response = await makeApiRequest(
    "PATCH",
    `multimedia-categories/update-categories-order/`,
    {
      category,
      oldIndex,
      newIndex,
    }
  );
  return response.success;
};

const deleteMultimediaCategory = async (
  data: MultimediaCategory
): Promise<any> => {
  let response = await makeApiRequest(
    "DELETE",
    "multimedia-categories/" + data.id
  );
  return response;
};

/**
 *
 * @returns END MULTIMEDIA CATEGORY
 */

/**
 *
 * @returns START VIDEO
 */

const getVideo = async (
  seasonNumber: number,
  programID: number
): Promise<Video[]> => {
  //  multimedia?page=1&limit=9999&season=1&programId=1
  const response = await makeApiRequest(
    "GET",
    `multimedia?page=1&limit=9999&season=${seasonNumber}&programId=${programID}&includeInactive=true`
  );
  return response.multimedia;
};

const addVideo = async (data: EditVideo): Promise<Video> => {
  const response = await makeApiRequest("POST", `multimedia`, data);
  return response.multimedia;
};

const updateVideo = async (data: Partial<EditVideo>): Promise<Video> => {
  const response = await makeApiRequest("PATCH", `multimedia/` + data.id, data);
  return response.multimedia;
};

const updateVideosEpisodeNumbers = async (
  video: Video,
  oldIndex: number,
  newIndex: number
): Promise<boolean> => {
  const response = await makeApiRequest(
    "PATCH",
    `multimedia/update-episode-numbers/`,
    {
      video,
      oldIndex,
      newIndex,
    }
  );
  return response.success;
};

const deleteVideo = async (id: string): Promise<any> => {
  const response = await makeApiRequest("DELETE", "multimedia/" + id);
  return response;
};

const initUploadVideo = async (
  size: number,
  name: string
): Promise<{ uploadLink: string }> => {
  const response = await makeApiRequest("POST", "multimedia-upload/init", {
    size,
    name,
  });

  return response;
};

const getVideoFolders = async (
  page?: number,
  limit?: number,
  query?: string
): Promise<MediaFolder[] | undefined> => {
  try {
    const response = await makeApiRequest(
      "GET",
      `admin/vimeo/folders${page ? `?page=${page}` : ""}${
        limit ? `&limit=${limit}` : ""
      }${query ? `&query=${query}` : ""}`
    );

    if (!response || response.status === "Failure") {
      throw new Error("Vimeo error");
    }

    return response.map((item: any) => toMediaFolder(item));
  } catch (error) {
    return undefined;
  }
};

const getUploadedVideos = async (
  folderId?: MediaFolder["projectId"],
  page?: number,
  limit?: number,
  query?: string
): Promise<{ videos: Media[]; videosTotal?: number } | undefined> => {
  try {
    const response = await makeApiRequest(
      "GET",
      `admin/vimeo/video${folderId ? `?projectId=${folderId}` : ""}${
        page ? (folderId ? `&page=${page}` : `?page=${page}`) : ""
      }${limit ? `&limit=${limit}` : ""}${query ? `&query=${query}` : ""}`
    );

    if (!response || response.status === "Failure") {
      throw new Error("Vimeo error");
    }

    return {
      videos: response.videos.map((item: any) => toMedia(item)),
      videosTotal: response.videosTotal,
    };
  } catch (error) {
    return undefined;
  }
};

const getVimeoUser = async (): Promise<VimeoUser | undefined> => {
  try {
    const response = await makeApiRequest("GET", `admin/vimeo/user`);

    if (!response || response.status === "Failure") {
      throw new Error("Vimeo error");
    }

    return toVimeoUser(response);
  } catch (error) {
    return undefined;
  }
};

/**
 *
 * @returns END VIDEO
 */

/**
 *
 * @returns START BANNER
 */

const getBanners = async (): Promise<Banner[]> => {
  const response = await makeApiRequest(
    "GET",
    `banner?page=1&limit=9999&includeInactive=true`
  );
  return response.banners;
};

const addBanner = async (data: EditBanner): Promise<Banner> => {
  const response = await makeApiRequest("POST", `banner`, data);
  return response.banner;
};

const updateBanner = async (data: EditBanner): Promise<Banner> => {
  const response = await makeApiRequest("PATCH", `banner/` + data.id, data);
  return response.banner;
};

const deleteBanner = async (id: string): Promise<any> => {
  const response = await makeApiRequest("DELETE", "banner/" + id);
  return response;
};

/**
 *
 * @returns END BANNER
 */

/**
 *
 * @returns START AD CIRCUIT
 */

const getAdCircuits = async (): Promise<AdCircuit[]> => {
  const response = await makeApiRequest("GET", `ad-circuit`);
  return response.adCircuits;
};

const getAdCircuitByID = async (id: string): Promise<AdCircuit> => {
  const response = await makeApiRequest("GET", "ad-circuit/" + id);
  return response.adCircuit;
};

const addAdCircuit = async (data: EditAdCircuit): Promise<AdCircuit> => {
  const response = await makeApiRequest("POST", `ad-circuit`, data);
  return response.adCircuit;
};

const updateAdCircuit = async (data: EditAdCircuit): Promise<AdCircuit> => {
  const response = await makeApiRequest("PATCH", `ad-circuit/` + data.id, data);
  return response.adCircuit;
};

const deleteAdCircuit = async (id: string): Promise<any> => {
  const response = await makeApiRequest("DELETE", "ad-circuit/" + id);
  return response;
};

/**
 *
 * @returns END AD CIRCUIT
 */

/**
 *
 * @returns START AD
 */

const getAds = async (id: string): Promise<Ad[]> => {
  const response = await makeApiRequest("GET", `ad?adCircuitId=${id}`);
  return response.ads;
};

const addAd = async (data: EditAd): Promise<Ad> => {
  const response = await makeApiRequest("POST", `ad`, data);
  return response.ad;
};

const updateAd = async (data: EditAd): Promise<Ad> => {
  const response = await makeApiRequest("PATCH", `ad/` + data.id, data);
  return response.ad;
};

const deleteAd = async (id: string): Promise<any> => {
  const response = await makeApiRequest("DELETE", "ad/" + id);
  return response;
};

/**
 *
 * @returns END AD
 */

/**
 *
 * @returns START ICON
 */

const getIcons = async (): Promise<Icon[]> => {
  const response = await makeApiRequest("GET", `icon`);
  return response.icons;
};

const addIcon = async (data: Icon): Promise<Icon> => {
  const response = await makeApiRequest("POST", `icon`, data);
  return response;
};

const deleteIcon = async (id: number): Promise<any> => {
  const response = await makeApiRequest("DELETE", `icon/${id}`);
  return response;
};

/**
 *
 * @returns END ICON
 */

/**
 *
 * @returns START MENU LINK
 */

const getMenuLinks = async (): Promise<MenuLink[]> => {
  const response = await makeApiRequest("GET", "menu-link");
  return response.menuLinks;
};

const addMenuLink = async (data: EditMenuLink): Promise<MenuLink> => {
  const response = await makeApiRequest("POST", "menu-link", data);
  return response.menuLink;
};

const updateMenuLink = async (data: EditMenuLink): Promise<MenuLink> => {
  const response = await makeApiRequest("PATCH", `menu-link/${data.id}`, data);
  return response.menuLink;
};

const deleteMenuLink = async (id: number): Promise<any> => {
  const response = await makeApiRequest("DELETE", `menu-link/${id}`);
  return response;
};

/**
 *
 * @returns END MENU LINK
 */

/**
 *
 * @returns START FOOTER ROW
 */

const getFooterRows = async (): Promise<FooterRow[]> => {
  const response = await makeApiRequest("GET", "footer");
  return response.footerRows;
};

const addFooterRow = async (data: FooterRow): Promise<FooterRow> => {
  const response = await makeApiRequest("POST", "footer", data);
  return response.footerRow;
};

const updateFooterRow = async (data: FooterRow): Promise<FooterRow> => {
  const response = await makeApiRequest("PATCH", `footer/${data.id}`, data);
  return response.footerRow;
};

const deleteFooterRow = async (id: number): Promise<any> => {
  const response = await makeApiRequest("DELETE", `footer/${id}`);
  return response;
};

/**
 *
 * @returns END FOOTER ROW
 */

/**
 *
 * @returns START STREAMING
 */

const getStreaming = async (): Promise<Streaming> => {
  const response = await makeApiRequest("GET", "streaming");
  return response.streaming;
};

const addStreaming = async (data: Streaming): Promise<Streaming> => {
  const response = await makeApiRequest("POST", "streaming", data);
  return response.streaming;
};

const updateStreaming = async (data: Streaming): Promise<Streaming> => {
  const response = await makeApiRequest("PATCH", `streaming/${data.id}`, data);
  return response.streaming;
};

/**
 *
 * @returns END STREAMING
 */

/**
 *
 * @returns START FIRESTICK CONFIG
 */

const getFirestickConfig = async (): Promise<FirestickConfig> => {
  const response = await makeApiRequest("GET", "firestick-config");
  return response.firestickConfig;
};

const addFirestickConfig = async (
  data: FirestickConfig
): Promise<FirestickConfig> => {
  const response = await makeApiRequest("POST", "firestick-config", data);
  return response.firestickConfig;
};

const updateFirestickConfig = async (
  data: FirestickConfig
): Promise<FirestickConfig> => {
  const response = await makeApiRequest(
    "PATCH",
    `firestick-config/${data.id}`,
    data
  );
  return response.firestickConfig;
};

/**
 *
 * @returns END FIRESTICK CONFIG
 */

/**
 *
 * @returns START PUSH NOTIFICATIONS
 */

const getPushNotifications = async (): Promise<PushNotification[]> => {
  const response = await makeApiRequest("GET", "push-notification");

  return response.pushNotifications;
};

const addPushNotification = async (
  data: PushNotification
): Promise<PushNotification> => {
  const response = await makeApiRequest("POST", "push-notification", data);

  return response.pushNotification;
};

/**
 *
 * @returns END PUSH NOTIFICATIONS
 */

const api = {
  getCurrentUser,
  login,
  changePassword,
  lostPassword,
  changeSessionUserPassword,
  getUsers,
  addUser,
  updateUser,
  deleteUser,
  getArticles,
  addArticle,
  updateArticle,
  deleteArticle,
  getProgramCategories,
  addProgramCategory,
  updateProgramCategory,
  updateProgramCategoriesOrder,
  deleteProgramCategory,
  uploadFileToBucket,
  getProgram,
  getProgramByID,
  addProgram,
  updateProgram,
  deleteProgram,
  deleteSeason,
  getMultimediaCategories,
  addMultimediaCategory,
  updateMultimediaCategory,
  updateMultimediaCategoriesOrder,
  deleteMultimediaCategory,
  getVideo,
  addVideo,
  updateVideo,
  updateVideosEpisodeNumbers,
  deleteVideo,
  initUploadVideo,
  getUploadedVideos,
  getBanners,
  addBanner,
  updateBanner,
  deleteBanner,
  getAdCircuits,
  getAdCircuitByID,
  addAdCircuit,
  updateAdCircuit,
  deleteAdCircuit,
  getAds,
  addAd,
  updateAd,
  deleteAd,
  getVideoFolders,
  getVimeoUser,
  getIcons,
  addIcon,
  deleteIcon,
  getMenuLinks,
  addMenuLink,
  updateMenuLink,
  deleteMenuLink,
  getFooterRows,
  addFooterRow,
  updateFooterRow,
  deleteFooterRow,
  getStreaming,
  addStreaming,
  updateStreaming,
  getFirestickConfig,
  addFirestickConfig,
  updateFirestickConfig,
  getPushNotifications,
  addPushNotification,
};

export default api;
